<script setup>
import IEventItem from '@components/IEventItem/IEventItem.vue';
import ITeams from '@components/ITeams/ITeams.vue';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import useSiteStore from '@vueApp/store/modules/site';
import {
  computed,
  watch,
  ref,
  onMounted,
} from 'vue';
import useCartStore from '@vueApp/store/modules/cart';
import EventItemsSkeleton from './EventItemsSkeleton.vue';
import EventsItemsEmpty from './EventsItemsEmpty.vue';

const store = useSiteStore();
const cartStore = useCartStore();

store.searchSubscriptions();
store.searchServices();
store.searchPerformances();
store.searchFriendlyMatches({
  dateRange: [],
  objectTags: [],
  nomIds: [35],
});
store.searchKhlMatches({
  dateRange: [],
  objectTags: [],
  nomIds: [13],
});
store.searchMhlMatches({
  dateRange: [],
  objectTags: [],
  nomIds: [45],
});

const countsEventGrouped = ref({});

const loadingData = computed(
  () => store.getSubscriptionsState.loading
    || store.getServicesState.loading
    || store.getPerformancesState.loading
    || store.getFriendlyMatchesState.loading
    || store.getKhlMatchesState.loading
    || store.getMhlMatchesState.loading,
);

const showFriendlyMatches = computed(
  () => !!store.getFriendlyMatchesState.items.length
    && (!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(5)),
);

const showKhlMatches = computed(
  () => !!store.getKhlMatchesState.items.length
    && (!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(5)),
);

const showMhlMatches = computed(
  () => !!store.getMhlMatchesState.items.length
    && (!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(5)),
);

const showSubscriptions = computed(
  () => !!store.getSubscriptionsState.items.length
    && (!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(2)),
);

const showServices = computed(
  () => !!store.getServicesState.items.length
    && (!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(3)),
);

const showPerformances = computed(
  () => !!store.getPerformancesState.items.length
    && (!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(4)),
);

async function getCountEventInCart() {
  await cartStore
    .getCartGroupEvent()
    .then((res) => {
      countsEventGrouped.value = res;
    });
}

watch(loadingData, () => (loadingData.value ? $('#js-overlay').show() : $('#js-overlay').hide()));

onMounted(() => {
  getCountEventInCart();
});
</script>

<template>
  <event-items-skeleton :is-data-loading="loadingData">
    <events-items-empty>
      <div v-if="showSubscriptions" class="mb-5">
        <h3 class="event-item__title">
          {{ translator.t("main", "Season tickets") }}
        </h3>
        <div class="events__items">
          <i-event-item
            v-for="event in store.getSubscriptionsState.items"
            :key="event.id"
            :event="event"
            :cart-count="countsEventGrouped[event.id] ?? 0"
          />
        </div>
        <div v-if="store.getSubscriptionsState.showLoadMore" class="text-center">
          <button
            class="i-btn i-btn_outlined i-btn_wide-mobile"
            @click="store.loadMoreSubscriptions"
          >
            {{ translator.t("main", "More") }}
          </button>
        </div>
      </div>

      <div v-if="showFriendlyMatches" class="mb-5">
        <h3 class="event-item__title">
          {{ translator.t("main", "Friendly matches") }}
        </h3>
        <div class="events__items">
          <i-event-item
            v-for="event in store.getFriendlyMatchesState.items"
            :key="event.id"
            :event="event"
            :is-package="event.eventType === 'Пакет'"
            :cart-count="countsEventGrouped[event.id] ?? 0"
          >
            <template v-if="event.eventType !== 'Пакет'" #name>
              <i-teams
                :first-team="event.participants.team?.homeTeam || event.participants.singular"
                :second-team="event.participants.team?.guestTeam"
                :to="!event.settings.isSoon ? event.pageLink : ''"
                :is-home-logo-with-opponent="event.settings.isHomeLogoWithOpponent"
                :name="event.name"
                class="teams_main"
              />
            </template>
          </i-event-item>
        </div>
        <div v-if="store.getFriendlyMatchesState.showLoadMore" class="text-center">
          <button
            class="i-btn i-btn_outlined i-btn_wide-mobile"
            @click="store.loadMoreFriendlyMatches"
          >
            {{ translator.t("main", "More") }}
          </button>
        </div>
      </div>

      <div v-if="showKhlMatches" class="mb-5">
        <h3 class="event-item__title">
          {{ translator.t("main", "KHL matches") }}
        </h3>
        <div class="events__items">
          <i-event-item
            v-for="event in store.getKhlMatchesState.items"
            :key="event.id"
            :event="event"
            :is-package="event.eventType === 'Пакет'"
            :cart-count="countsEventGrouped[event.id] ?? 0"
          >
            <template v-if="event.eventType !== 'Пакет'" #name>
              <i-teams
                :first-team="event.participants.team?.homeTeam || event.participants.singular"
                :second-team="event.participants.team?.guestTeam"
                :to="!event.settings.isSoon ? event.pageLink : ''"
                :is-home-logo-with-opponent="event.settings.isHomeLogoWithOpponent"
                :name="event.name"
                class="teams_main"
              />
            </template>
          </i-event-item>
        </div>
        <div v-if="store.getFriendlyMatchesState.showLoadMore" class="text-center">
          <button
            class="i-btn i-btn_outlined i-btn_wide-mobile"
            @click="store.loadMoreFriendlyMatches"
          >
            {{ translator.t("main", "More") }}
          </button>
        </div>
      </div>

      <div v-if="showMhlMatches" class="mb-5">
        <h3 class="event-item__title">
          {{ translator.t("main", "MHL matches") }}
        </h3>
        <div class="events__items">
          <i-event-item
            v-for="event in store.getMhlMatchesState.items"
            :key="event.id"
            :event="event"
            :is-package="event.eventType === 'Пакет'"
            :cart-count="countsEventGrouped[event.id] ?? 0"
          >
            <template v-if="event.eventType !== 'Пакет'" #name>
              <i-teams
                :first-team="event.participants.team?.homeTeam || event.participants.singular"
                :second-team="event.participants.team?.guestTeam"
                :to="!event.settings.isSoon ? event.pageLink : ''"
                :is-home-logo-with-opponent="event.settings.isHomeLogoWithOpponent"
                :name="event.name"
                class="teams_main"
              />
            </template>
          </i-event-item>
        </div>
        <div v-if="store.getFriendlyMatchesState.showLoadMore" class="text-center">
          <button
            class="i-btn i-btn_outlined i-btn_wide-mobile"
            @click="store.loadMoreFriendlyMatches"
          >
            {{ translator.t("main", "More") }}
          </button>
        </div>
      </div>

      <div v-if="showPerformances" class="mb-5">
        <div class="fs-3 lh-sm mb-4">{{ translator.t("main", "Concerts & Shows") }}</div>
        <div class="events__items">
          <i-event-item
            v-for="event in store.getPerformancesState.items"
            :key="event.id"
            :event="event"
            :is-perfomance="true"
            :cart-count="countsEventGrouped[event.id] ?? 0"
          />
        </div>
        <div v-if="store.getPerformancesState.showLoadMore" class="text-center">
          <button
            class="i-btn i-btn_outlined i-btn_wide-mobile"
            @click="store.loadMorePerformances"
          >
            {{ translator.t("main", "More") }}
          </button>
        </div>
      </div>
    </events-items-empty>
  </event-items-skeleton>
</template>

<style lang="scss">
.events__items {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 30px;
  margin-left: 0;
  gap: 30px;

  .i-teams__name {
    font-size: 28px;
  }

  @media (max-width: $screen-xs) {
    gap: 16px;

    .i-teams__name {
      font-size: 16px;
    }
  }
}

.event-item {
  &__title {
    margin-bottom: 20px;
    font-weight: 400;
    font-size: 30px;
    color: $color;
  }

  @media (max-width: $screen-xs) {
    padding: 15px;

    &__title {
      margin-bottom: 16px;
      font-size: 24px;
    }

    &__title_small {
      content: '';
      bottom: 3px;
      left: 2px;
      font-weight: 600;
      font-size: 20px;
    }
  }
}
</style>
